import { format, parseISO } from "date-fns";
import { formatCurrency } from "@vaulzo/core/constants";
import { W, useChartGeometry, formatShort } from "@vaulzo/core/domain/chartGeometry";

// Re-exported for the Assets page, which labels its own figures with it.
export { formatShort };

// From the palette variables, so the chart follows light / dark. Set via
// style (not SVG attributes): attributes do not resolve var().
const LINE = "rgb(var(--vz-accent))";
const BASE = "rgb(var(--vz-gold))";
const LOSS = "rgb(var(--vz-danger))";
const GRID = "rgb(var(--vz-hairline))";
const START = "rgb(var(--vz-subtle))";
/**
 * Value-over-time line for the overview and the Assets tabs. Hover (or
 * touch) snaps to the nearest snapshot by date and reports its index up,
 * so the hero above can scrub too. Pass `base` to draw the money-put-in
 * floor and the gain band (Investments).
 */
export default function NetWorthChart({ points, base = null, hoverIndex, onHover, height = 200, id = "nw" }) {
  const g = useChartGeometry(points, base, height);
  if (!g) return null;
  const n = points.length;
  const H = height;

  const onPointerMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const f = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const t = g.t0 + f * g.tSpan;
    let best = 0;
    for (let i = 1; i < n; i++) if (Math.abs(g.pts[i].t - t) < Math.abs(g.pts[best].t - t)) best = i;
    if (best !== hoverIndex) onHover(best);
  };

  const hp = hoverIndex != null ? g.pts[hoverIndex] : null;
  const delta = hp ? hp.value - points[0].value : 0;
  const deltaPct = hp && points[0].value ? Math.abs((delta / points[0].value) * 100).toFixed(1) : "0.0";
  const hoverX = hp ? (g.xs[hoverIndex] / W) * 100 : 0;
  const hoverGain = hp && hp.b != null ? hp.value - hp.b : null;

  return (
    <div>
      {g.bands && (
        <div className="mb-[10px] flex flex-wrap gap-x-[18px] gap-y-1 text-[12px] text-vaulzo-muted">
          <LegendKey color={LINE} line label="Value" />
          <LegendKey color={BASE} line label="Money put in" />
          <LegendKey color="rgb(var(--vz-accent) / 0.3)" label="Market gain" />
          <LegendKey color="rgb(var(--vz-danger) / 0.3)" label="Below cost" />
        </div>
      )}
      <div
        className="relative w-full cursor-crosshair touch-none select-none"
        style={{ height: H }}
        onPointerMove={onPointerMove}
        onPointerDown={onPointerMove}
        onPointerLeave={() => onHover(null)}
      >
        <svg
          width="100%"
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="block overflow-visible"
          role="img"
          aria-label={`From ${format(parseISO(points[0].date), "d MMM yyyy")} to ${format(parseISO(points[n - 1].date), "d MMM yyyy")}, ${formatCurrency(points[0].value)} to ${formatCurrency(points[n - 1].value)}`}
        >
          <defs>
            <linearGradient id={`${id}-wash`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" style={{ stopColor: LINE }} stopOpacity={0.16} />
              <stop offset="100%" style={{ stopColor: LINE }} stopOpacity={0} />
            </linearGradient>
            {g.bands && (
              <>
                <clipPath id={`${id}-above`}><path d={g.bands.clipAbove} /></clipPath>
                <clipPath id={`${id}-below`}><path d={g.bands.clipBelow} /></clipPath>
              </>
            )}
          </defs>
          {g.grid.map((row) => (
            <line key={row.label} x1="0" x2={W} y1={row.y} y2={row.y} style={{ stroke: GRID }} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          <line x1="0" x2={W} y1={g.startY} y2={g.startY} style={{ stroke: START }} strokeWidth="1" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />
          {g.bands ? (
            <>
              <path d={g.bands.area} style={{ fill: BASE }} fillOpacity={0.13} />
              <path d={g.bands.band} style={{ fill: LINE }} fillOpacity={0.22} clipPath={`url(#${id}-above)`} />
              <path d={g.bands.band} style={{ fill: LOSS }} fillOpacity={0.22} clipPath={`url(#${id}-below)`} />
              {g.bands.lineEst && (
                <path d={g.bands.lineEst} fill="none" style={{ stroke: BASE }} strokeWidth="1.5" strokeDasharray="4 4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              )}
              {g.bands.line && (
                <path d={g.bands.line} fill="none" style={{ stroke: BASE }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              )}
            </>
          ) : (
            <path d={g.area} fill={`url(#${id}-wash)`} />
          )}
          <path d={g.line} fill="none" style={{ stroke: LINE }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>

        {/* Value gridline labels, tucked above each line on the right */}
        {g.grid.map((row) => (
          <div
            key={row.label}
            className={`num pointer-events-none absolute right-0 -translate-y-full pb-[3px] text-[11px] text-vaulzo-fainter ${row.hidden ? "opacity-0" : ""}`}
            style={{ top: row.y }}
          >
            {row.label}
          </div>
        ))}

        {g.bands?.estUntil && (
          <>
            <div className="pointer-events-none absolute inset-y-0 w-0 border-l border-dashed border-vaulzo-gold/50" style={{ left: `${g.bands.estUntil.x}%` }} />
            <div
              className="pointer-events-none absolute bottom-1 -translate-x-full whitespace-nowrap pr-[6px] text-[10px] uppercase tracking-[0.04em]"
              style={{ left: `${g.bands.estUntil.x}%`, color: BASE }}
            >
              {g.bands.estUntil.label}
            </div>
          </>
        )}

        <Marker point={g.high} filled labelBelow={g.high.below} />
        {g.low && <Marker point={g.low} labelBelow={!g.low.above} />}
        {g.showEnd && (
          <div
            className="pointer-events-none absolute left-full h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-vaulzo-accent shadow-[0_0_0_2px_rgb(var(--vz-bg))]"
            style={{ top: g.endY }}
          />
        )}

        {hp && (
          <>
            <div className="pointer-events-none absolute inset-y-0 w-0 border-l border-vaulzo-bar" style={{ left: `${hoverX}%` }} />
            <div
              className="pointer-events-none absolute h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-vaulzo-accent shadow-[0_0_0_2px_rgb(var(--vz-surface)),0_0_0_6px_rgb(var(--vz-accent)/0.18)]"
              style={{ left: `${hoverX}%`, top: g.yOf(hp.value) }}
            />
            <div
              className="pointer-events-none absolute top-2 z-[5] whitespace-nowrap rounded-xl border border-vaulzo-hairline bg-vaulzo-surface px-3 py-2 shadow-[0_6px_20px_rgba(23,24,26,0.10)]"
              style={{
                left: `${hoverX}%`,
                transform: hoverX > 62 ? "translateX(calc(-100% - 14px))" : "translateX(14px)",
              }}
            >
              <div className="text-[11px] text-vaulzo-fainter">{format(parseISO(hp.date), "d MMM yyyy")}</div>
              <div className="num text-[14px] font-semibold text-vaulzo-ink">{formatCurrency(hp.value)}</div>
              <div className="num text-[12px]" style={{ color: delta >= 0 ? LINE : LOSS }}>
                {delta >= 0 ? "↑" : "↓"} {formatCurrency(Math.abs(delta))} · {deltaPct}%{" "}
                <span className="text-vaulzo-fainter">vs start</span>
              </div>
              {hoverGain != null && (
                <div className="num mt-1 border-t border-vaulzo-row pt-1 text-[12px] text-vaulzo-muted">
                  <div><Swatch color={BASE} /> Put in {formatCurrency(hp.b)}</div>
                  <div><Swatch color={hoverGain >= 0 ? LINE : LOSS} /> Market {hoverGain >= 0 ? "+" : "−"}{formatCurrency(Math.abs(hoverGain))}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Date axis: positioned by time, not by index */}
      <div className="relative mt-[10px] h-4">
        {g.ticks.map((t) => (
          <span
            key={t.label + t.x}
            className="absolute whitespace-nowrap text-[11px] text-vaulzo-faint"
            style={{ left: `${t.x}%`, transform: `translateX(${t.shift})` }}
          >
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Period high / low: a dot on the line plus "₹51.74L · 21 Sep" beside it.
function Marker({ point, filled = false, labelBelow }) {
  return (
    <>
      <div
        className={`pointer-events-none absolute h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full ${
          filled ? "bg-vaulzo-accent shadow-[0_0_0_2px_rgb(var(--vz-bg))]" : "box-border border-2 border-vaulzo-accent bg-vaulzo-bg"
        }`}
        style={{ left: `${point.x}%`, top: point.y }}
      />
      <div
        className="pointer-events-none absolute whitespace-nowrap text-[11px] text-vaulzo-faint"
        style={{
          left: `${point.x}%`,
          top: point.y,
          transform: `translate(${point.shift}, ${labelBelow ? "9px" : "calc(-100% - 9px)"})`,
        }}
      >
        <span className="num font-semibold text-vaulzo-ink">{point.value}</span> · {point.date}
      </div>
    </>
  );
}

function Swatch({ color }) {
  return <span className="mr-[6px] inline-block h-2 w-2 rounded-[2px] align-baseline" style={{ background: color }} />;
}

function LegendKey({ color, line = false, label }) {
  return (
    <span className="flex items-center gap-[6px]">
      <span className={line ? "h-[3px] w-3 rounded-sm" : "h-2 w-3 rounded-[2px]"} style={{ background: color }} />
      {label}
    </span>
  );
}
